import { useEffect, useState } from "react";

interface DatePickerProps {
  value: string;
  onChange: (date: string) => void;
  placeholder: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder,
}) => {
  /* ---------------- Utility Functions (must be above hooks) ---------------- */

  function parseDate(dateString: string): Date | null {
    if (!dateString) return null;

    const date = new Date(dateString);

    if (isNaN(date.getTime())) return null;

    return date;
  }

  const formatDate = (date: Date | null): string => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const formatDateDDMMYYYY = (date: Date | null): string => {
    if (!date) return "";

    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();

    return `${day}-${month}-${year}`;
  };

  /* ---------------- State ---------------- */

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string>(value);

  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    const parsedDate = value ? parseDate(value) : null;
    return parsedDate || new Date();
  });

  const [showMonthYearPicker, setShowMonthYearPicker] =
    useState<boolean>(false);

  /* ---------------- Effects ---------------- */

  useEffect(() => {
    setSelectedDate(value);

    if (value) {
      const parsedDate = parseDate(value);

      if (parsedDate) {
        setCurrentMonth(parsedDate);
      }
    }
  }, [value]);

  /* ---------------- Calendar Logic ---------------- */

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDay = new Date(year, month, 1);

    const days: Date[] = [];

    const startDate = new Date(firstDay);

    startDate.setDate(startDate.getDate() - firstDay.getDay());

    for (let i = 0; i < 42; i++) {
      days.push(new Date(startDate));
      startDate.setDate(startDate.getDate() + 1);
    }

    return days;
  };

  const handleDateSelect = (date: Date): void => {
    const formatted = formatDate(date);

    setSelectedDate(formatted);

    onChange(formatted);

    setIsOpen(false);
  };

  const navigateMonth = (direction: number): void => {
    const newMonth = new Date(currentMonth);

    newMonth.setMonth(newMonth.getMonth() + direction);

    setCurrentMonth(newMonth);
  };

  const handleMonthYearSelect = (month: number, year: number): void => {
    const newDate = new Date(year, month, 1);

    setCurrentMonth(newDate);

    setShowMonthYearPicker(false);
  };

  const getCurrentYear = (): number => currentMonth.getFullYear();

  const getCurrentMonth = (): number => currentMonth.getMonth();

  const generateYears = (): number[] => {
    const currentYear = new Date().getFullYear();

    const years: number[] = [];

    for (let i = currentYear - 80; i <= currentYear; i++) {
      years.push(i);
    }

    return years;
  };

  /* ---------------- Constants ---------------- */

  const monthNames: string[] = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const days: Date[] = getDaysInMonth(currentMonth);

  const selectedDateObj: Date | null = parseDate(selectedDate);

  /* ---------------- UI ---------------- */

  return (
    <div className="flex flex-col">
      <p className="text-text-base font-semibold text-[10px]">DOB</p>
      <div
        className="flex flex-row w-[50%] max-h-[37px] border border-border-subtle rounded-lg bg-icon-white px-[12px] py-[8px] cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}>
        <div className="flex flex-col space-y-[4px] flex-1">
          <p className="text-text-base font-normal text-[12px]">
            {formatDateDDMMYYYY(selectedDateObj)}
          </p>
        </div>

        <div className="flex items-center">
          <span className="material-symbols-outlined text-text-base text-[18px]">
            calendar_today
          </span>
        </div>
      </div>

      {isOpen && (
        <div className="mt-[4px] bg-bg-base border border-border-subtle rounded-lg p-4">
          {!showMonthYearPicker ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-2 hover:bg-bg-button-secondary rounded-lg">
                  <span className="material-symbols-outlined">
                    chevron_left
                  </span>
                </button>

                <button
                  onClick={() => setShowMonthYearPicker(true)}
                  className="text-text-base font-semibold text-[16px] hover:bg-bg-button-secondary px-3 py-1 rounded-lg">
                  {monthNames[currentMonth.getMonth()]}{" "}
                  {currentMonth.getFullYear()}
                </button>

                <button
                  onClick={() => navigateMonth(1)}
                  className="p-2 hover:bg-bg-button-secondary rounded-lg">
                  <span className="material-symbols-outlined">
                    chevron_right
                  </span>
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                  (day) => (
                    <div
                      key={day}
                      className="text-center text-[12px] font-medium p-2">
                      {day}
                    </div>
                  ),
                )}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {days.map((date, index) => {
                  const isCurrentMonth =
                    date.getMonth() === currentMonth.getMonth();

                  const isSelected =
                    selectedDateObj !== null &&
                    date.getDate() === selectedDateObj.getDate() &&
                    date.getMonth() === selectedDateObj.getMonth() &&
                    date.getFullYear() === selectedDateObj.getFullYear();

                  const isToday =
                    new Date().toDateString() === date.toDateString();

                  return (
                    <button
                      key={index}
                      onClick={() => handleDateSelect(date)}
                      className={`
                        p-2 text-[14px] rounded-lg transition-colors
                        ${!isCurrentMonth ? "text-text-disabled" : "text-text-base"}
                        ${isSelected ? "bg-bg-button-primary text-white" : ""}
                        ${isToday && !isSelected ? "bg-bg-button-secondary" : ""}
                        ${isCurrentMonth && !isSelected ? "hover:bg-bg-button-secondary" : ""}
                      `}>
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-[16px] font-semibold">
                  Select Month & Year
                </h3>

                <button
                  onClick={() => setShowMonthYearPicker(false)}
                  className="p-2 hover:bg-bg-button-secondary rounded-lg">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-4">
                {monthNames.map((month, index) => (
                  <button
                    key={month}
                    onClick={() =>
                      handleMonthYearSelect(index, getCurrentYear())
                    }
                    className={`
                      p-2 text-[14px] rounded-lg
                      ${
                        index === getCurrentMonth()
                          ? "bg-bg-button-primary text-white"
                          : "hover:bg-bg-button-secondary"
                      }
                    `}>
                    {month.slice(0, 3)}
                  </button>
                ))}
              </div>

              <div className="max-h-32 overflow-y-auto">
                <div className="grid grid-cols-4 gap-2">
                  {generateYears().map((year) => (
                    <button
                      key={year}
                      onClick={() =>
                        handleMonthYearSelect(getCurrentMonth(), year)
                      }
                      className={`
                        p-2 text-[14px] rounded-lg
                        ${
                          year === getCurrentYear()
                            ? "bg-bg-button-primary text-white"
                            : "hover:bg-bg-button-secondary"
                        }
                      `}>
                      {year}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
